// chartRendered.js
import React from "react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  RadialBarChart,
  RadialBar,
  Treemap,
  ScatterChart,
  Scatter,
  FunnelChart,
  Funnel,
  LabelList,
  ComposedChart,
} from "recharts";
import { FaMale, FaFemale, FaBirthdayCake, FaUtensils, FaCar, FaPaw, FaUser } from "react-icons/fa";
import CrosstabTable from "./CrosstabTable";

const COLORS = [
  "#6366f1",
  "#8b5cf6",
  "#ec4899",
  "#f43f5e",
  "#f59e0b",
  "#10b981",
  "#06b6d4",
  "#3b82f6",
];

/* ----------------------- UNIVERSAL % LABEL ---------------------- */
const NamePercentLabel = ({ x, y, width, index, dataKey, data }) => {
  const item = data[index];
  if (!item) return null;

  const name = item.name || "";
  const percent =
    typeof item.percent === "number"
      ? `${item.percent.toFixed(1)}%`
      : item.percent || "";

  return (
    <text
      x={x + width / 2}
      y={y - 5}
      textAnchor="middle"
      fontSize={12}
      fill="#111"
      fontWeight="600"
    >
      {`${name} (${percent})`}
    </text>
  );
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const d = payload[0].payload;

    return (
      <div
        style={{
          background: "white",
          padding: "10px",
          borderRadius: "8px",
          border: "1px solid #e5e7eb",
        }}
      >
        <p>
          <strong>{label}</strong>
        </p>
        <p>Value: {d.value}</p>
        <p>Percent: {d.percent}%</p>
        <p>Base: {d.base}</p>
      </div>
    );
  }
  return null;
};

/* ------------------ Pictogram (admin type) rendering ------------------ */
const GENDER_ICON_MATCH = {
  male: FaMale, mann: FaMale, m: FaMale, boy: FaMale, man: FaMale,
  female: FaFemale, frau: FaFemale, f: FaFemale, girl: FaFemale, woman: FaFemale,
};
const pickGenderIcon = (name) => GENDER_ICON_MATCH[(name || "").toString().trim().toLowerCase()] || FaUser;

const ADMIN_TYPE_ICON = {
  ShapeAge: FaBirthdayCake,
  ShapeFood: FaUtensils,
  ShapeVehicle: FaCar,
  ShapeAnimal: FaPaw,
  ShapePerson: FaUser,
};

const PictogramRenderer = ({ data, adminType }) => {
  if (!Array.isArray(data) || data.length === 0) return <div>No data available</div>;

  if (adminType === "Card" || !adminType) {
    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        {data.map((row, i) => (
          <div
            key={i}
            style={{
              flex: "1 1 120px",
              minWidth: 120,
              border: "1px solid #e2e8f0",
              borderRadius: 8,
              padding: 14,
              textAlign: "center",
              background: "#fff",
            }}
          >
            <div style={{ fontSize: 22, fontWeight: 700, color: "#4f46e5" }}>
              {typeof row.percent === "number" ? `${row.percent.toFixed(1)}%` : row.percent ?? "-"}
            </div>
            <div style={{ fontSize: 13, color: "#475569", marginTop: 4 }}>{row.name}</div>
            <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>n={row.value}</div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 18 }}>
      {data.map((row, i) => {
        const Icon = adminType === "ShapeGender" ? pickGenderIcon(row.name) : ADMIN_TYPE_ICON[adminType] || FaUser;
        const percent = Math.max(0, Math.min(100, Number(row.percent) || 0));

        return (
          <div key={i} style={{ width: 90, textAlign: "center" }}>
            <div style={{ position: "relative", width: 64, height: 64, margin: "0 auto" }}>
              <Icon size={64} color="#e2e8f0" />
              <div
                style={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  width: "100%",
                  height: `${percent}%`,
                  overflow: "hidden",
                }}
              >
                <Icon size={64} color="#6366f1" style={{ position: "absolute", bottom: 0, left: 0 }} />
              </div>
            </div>
            <div style={{ fontSize: 12, fontWeight: 600, marginTop: 6, color: "#334155" }}>
              {percent.toFixed(1)}%
            </div>
            <div style={{ fontSize: 11, color: "#64748b" }}>{row.name}</div>
          </div>
        );
      })}
    </div>
  );
};

/* ------------------ NEW: helpers for nested crosstab charts ------------------ */

// Build flattened chart data (rows become names, columns become keys)
const buildCombinedChartData = (data, usePercent = true) => {
  if (!data || !data.table || !data.rowValues) return [];
  const { rowValues, colValues, table } = data;

  const validColumns = colValues.filter((col) => col !== "__MISSING__");
  const validRows = rowValues.filter((row) => row !== "__MISSING__");

  return validRows.map((row, rIndex) => {
    const actualIndex = rowValues.indexOf(row);
    const rowData = { name: row };
    validColumns.forEach((col) => {
      const raw = table[actualIndex]?.[col];
      rowData[col] = usePercent ? raw?.percent ?? 0 : raw?.value ?? 0;
    });
    return rowData;
  });
};

// Build multiple charts data for Option A (one chart per rowLeaf × colLeaf pair)
// This creates a small chart dataset per pair where x-axis can be categories present in original table rows
const buildMultipleChartsData = (data) => {
  if (!data || !data.table) return [];

  const rows = data.rowLeaves || data.rowValues || [];
  const cols = data.colLeaves || data.colValues || [];

  // For each (row leaf × col leaf) create a tiny dataset: the table already stores rows; we'll extract the single row that matches the row leaf
  const pairs = [];
  rows.forEach((r) => {
    cols.forEach((c) => {
      // find the table row object whose rowLabel matches r
      const tableRow = data.table.find(
        (t) =>
          String(t.rowLabel) === String(r) ||
          String(t.rowLabel).endsWith(String(r))
      );
      if (!tableRow) return;
      const value = tableRow[c]?.value ?? 0;
      const percent = tableRow[c]?.percent ?? 0;
      pairs.push({
        row: r,
        col: c,
        data: [
          {
            name: `${r} × ${c}`,
            value,
            percent,
            base: tableRow[c]?.base ?? data?.table?.base ?? null,
          },
        ],
      });
    });
  });

  return pairs;
};

/* ------------------------ MAIN COMPONENT ------------------------ */
export default function ChartRenderer({
  type,
  data,
  isCrosstab,
  kpi: kpiProp,
}) {
  // kpiProp optional; some callers pass full KPI, others only pass data
  const kpiLike = kpiProp || {
    qTitle: data?.qTitle,
    data,
    rowVarsTree: data?.rowVarsTree,
    colVarsTree: data?.colVarsTree,
    chartMode: data?.chartMode,
  };

  // console.log ("🎨 ChartRenderer called with:", { type, isCrosstab, hasData: !!data });

  // validation
  if (isCrosstab) {
    if (!data || !data.table || !data.rowValues || !data.colValues) {
      console.error("❌ Invalid crosstab data:", data);
      return <div>No crosstab data available</div>;
    }
  } else {
    if (!data || (Array.isArray(data) && data.length === 0)) {
      console.error("❌ Invalid regular chart data:", data);
      return <div>No data available</div>;
    }
  }

  const chart = type?.toLowerCase();
  const chartMode = kpiLike.chartMode || data?.chartMode || "B"; // default to combined (B) for backward compatibility

  // ------------------ CROSSTAB handling (new flexible flow) ------------------
  if (isCrosstab && data.table) {
    // Option A: multiple charts (one per rowLeaf × colLeaf)
    if (chartMode === "A") {
      const pairs = buildMultipleChartsData(data);

      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
          {pairs.map((p, idx) => (
            <div key={idx}>
              <h4 style={{ margin: "6px 0" }}>
                {p.row} × {p.col}
              </h4>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={p.data}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="value" fill={COLORS[idx % COLORS.length]}>
                    <LabelList position="top" formatter={(v) => `${v}`} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ))}
        </div>
      );
    }

    // Option B: combined single chart (flattened)
    if (chartMode === "B") {
      const chartData = buildCombinedChartData(data, true); // use percent by default
      const seriesKeys = (data.colValues || []).filter(
        (c) => c !== "__MISSING__"
      );

      // If user chose stacked/ grouped variants, reuse your existing switch cases (we fall into same switch)
      switch (chart) {
        case "groupedcolumn":
          return (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                {seriesKeys.map((key, i) => (
                  <Bar key={i} dataKey={key} fill={COLORS[i % COLORS.length]}>
                    <LabelList
                      position="top"
                      formatter={(v) => `${v.toFixed ? v.toFixed(0) : v}%`}
                      fontSize={10}
                    />
                  </Bar>
                ))}
              </BarChart>
            </ResponsiveContainer>
          );

        case "stackedcolumn":
          return (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                {seriesKeys.map((key, i) => (
                  <Bar
                    key={i}
                    dataKey={key}
                    stackId="a"
                    fill={COLORS[i % COLORS.length]}
                  >
                    <LabelList
                      position="inside"
                      formatter={(v) => `${v.toFixed ? v.toFixed(0) : v}%`}
                      fill="#fff"
                      fontSize={10}
                    />
                  </Bar>
                ))}
              </BarChart>
            </ResponsiveContainer>
          );

        case "stackedbar":
          return (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={chartData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis type="category" dataKey="name" width={150} />
                <Tooltip />
                <Legend />
                {seriesKeys.map((key, i) => (
                  <Bar
                    key={i}
                    dataKey={key}
                    stackId="a"
                    fill={COLORS[i % COLORS.length]}
                  >
                    <LabelList
                      position="inside"
                      formatter={(v) => `${v.toFixed ? v.toFixed(0) : v}%`}
                      fill="#fff"
                      fontSize={10}
                    />
                  </Bar>
                ))}
              </BarChart>
            </ResponsiveContainer>
          );

        case "groupedbar":
          return (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={chartData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis type="category" dataKey="name" width={150} />
                <Tooltip />
                <Legend />
                {seriesKeys.map((key, i) => (
                  <Bar key={i} dataKey={key} fill={COLORS[i % COLORS.length]}>
                    <LabelList
                      position="right"
                      formatter={(v) => `${v.toFixed ? v.toFixed(0) : v}%`}
                      fontSize={10}
                    />
                  </Bar>
                ))}
              </BarChart>
            </ResponsiveContainer>
          );

        case "line":
          return (
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                {seriesKeys.map((key, i) => (
                  <Line
                    key={i}
                    type="monotone"
                    dataKey={key}
                    stroke={COLORS[i % COLORS.length]}
                    strokeWidth={2}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          );

        default:
          // fallback: render the nested table (uses your updated CrosstabTable API)
          return <CrosstabTable kpi={kpiLike} />;
      }
    }

    // default fallback, render your table
    return <CrosstabTable kpi={kpiLike} />;
  }

  // =================== REGULAR CHART HANDLING (unchanged) ===================
  // (keeps all your original cases exactly the same)
  const prepareStackedPercentData = (rawData) => {
    return rawData.map((row) => {
      const keys = Object.keys(row).filter(
        (k) => k !== "name" && k !== "percent" && k !== "base"
      );

      const total = keys.reduce((sum, k) => sum + (row[k] || 0), 0) || 1;

      const percentObj = {};
      keys.forEach((k) => {
        percentObj[k + "_percent"] = ((row[k] / total) * 100).toFixed(1);
      });

      return { ...row, ...percentObj };
    });
  };

  switch (chart) {
    case "bar":
      return (
        <ResponsiveContainer width="100%" height={350}>
          <BarChart layout="vertical" data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" />
            <YAxis dataKey="name" type="category" />
            <Tooltip content={<CustomTooltip />} />

            <Bar dataKey="value" fill="#4facfe">
              <LabelList
                content={(props) => <NamePercentLabel {...props} data={data} />}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      );

    case "column":
      return (
        <ResponsiveContainer width="100%" height={350}>
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip content={<CustomTooltip />} />

            <Bar dataKey="value" fill="#667eea">
              <LabelList
                content={(props) => <NamePercentLabel {...props} data={data} />}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      );

    case "stackedbar":
      const keys = Object.keys(data[0]).filter(
        (k) => k !== "name" && k !== "percent" && k !== "base"
      ) || ["value"];

      return (
        <ResponsiveContainer width="100%" height={350}>
          <BarChart data={data} stackOffset="expand">
            <CartesianGrid strokeDasharray="3 3" />

            <XAxis dataKey="name" />
            <YAxis tickFormatter={(v) => `${(v * 100).toFixed(0)}%`} />

            <Tooltip content={<CustomTooltip />} />

            {keys.map((key, i) => (
              <Bar
                key={i}
                dataKey={key}
                stackId="a"
                fill={COLORS[i % COLORS.length]}
              >
                <LabelList
                  position="top"
                  content={(props) => (
                    <NamePercentLabel {...props} data={data} dataKey={key} />
                  )}
                />
              </Bar>
            ))}
          </BarChart>
        </ResponsiveContainer>
      );

    case "line":
      return (
        <ResponsiveContainer width="100%" height={350}>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip content={<CustomTooltip />} />

            <Line
              type="monotone"
              dataKey="value"
              stroke="#4facfe"
              strokeWidth={3}
            >
              <LabelList
                content={(props) => <NamePercentLabel {...props} data={data} />}
              />
            </Line>
          </LineChart>
        </ResponsiveContainer>
      );

    case "area":
      return (
        <ResponsiveContainer width="100%" height={350}>
          <AreaChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip content={<CustomTooltip />} />

            <Area dataKey="value" stroke="#4facfe" fill="#a5b4fc">
              <LabelList
                content={(props) => <NamePercentLabel {...props} data={data} />}
              />
            </Area>
          </AreaChart>
        </ResponsiveContainer>
      );

    case "pie":
      return (
        <ResponsiveContainer width="100%" height={380}>
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              outerRadius={120}
              label={(d) => `${d.name} (${d.percent}%)`}
            >
              {data.map((d, i) => (
                <Cell key={i} fill={COLORS[i % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>
      );

    case "donut":
      return (
        <ResponsiveContainer width="100%" height={380}>
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              innerRadius={60}
              outerRadius={120}
              label={(d) => `${d.name} (${d.percent}%)`}
            >
              {data.map((d, i) => (
                <Cell key={i} fill={COLORS[i % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>
      );

    case "radar":
      return (
        <ResponsiveContainer width="100%" height={350}>
          <RadarChart data={data}>
            <PolarGrid />
            <PolarAngleAxis dataKey="name" />
            <Radar
              dataKey="value"
              stroke="#4facfe"
              fill="#4facfe"
              fillOpacity={0.6}
            />
            <Tooltip content={<CustomTooltip />} />
          </RadarChart>
        </ResponsiveContainer>
      );

    case "radialbar":
      return (
        <ResponsiveContainer width="100%" height={350}>
          <RadialBarChart innerRadius="20%" outerRadius="100%" data={data}>
            <RadialBar dataKey="value" label={{ fill: "#000" }} />
            <Tooltip content={<CustomTooltip />} />
          </RadialBarChart>
        </ResponsiveContainer>
      );

    case "treemap":
      return (
        <ResponsiveContainer width="100%" height={350}>
          <Treemap
            data={data}
            dataKey="value"
            nameKey="name"
            stroke="#fff"
            fill="#4facfe"
            content={(d) => `${d.name} (${d.percent}%)`}
          />
        </ResponsiveContainer>
      );

    case "funnel": {
      const sortedData = [...data].sort((a, b) => b.percent - a.percent);
      const FUNNEL_COLORS = [
        "#4e79a7",
        "#f28e2c",
        "#e15759",
        "#76b7b2",
        "#59a14f",
        "#edc949",
        "#af7aa1",
        "#ff9da7",
        "#9c755f",
        "#bab0ac",
      ];
      return (
        <div style={{ display: "flex", gap: "20px" }}>
          <div style={{ minWidth: "180px" }}>
            {sortedData.map((item, index) => (
              <div
                key={index}
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "6px",
                }}
              >
                <div
                  style={{
                    width: "14px",
                    height: "14px",
                    borderRadius: "4px",
                    backgroundColor:
                      FUNNEL_COLORS[index % FUNNEL_COLORS.length],
                    marginRight: "8px",
                  }}
                />
                <span>{item.name}</span>
              </div>
            ))}
          </div>

          <ResponsiveContainer width="100%" height={350}>
            <FunnelChart>
              <Funnel dataKey="value" data={sortedData} isAnimationActive>
                {sortedData.map((entry, index) => (
                  <Cell key={index} fill={COLORS[index % COLORS.length]} />
                ))}
                <LabelList
                  dataKey="percent"
                  position="inside"
                  formatter={(v) => `${v}%`}
                  fill="#fff"
                  fontSize={14}
                />
              </Funnel>
              <Tooltip content={<CustomTooltip />} />
            </FunnelChart>
          </ResponsiveContainer>
        </div>
      );
    }

    case "composed":
      return (
        <ResponsiveContainer width="100%" height={350}>
          <ComposedChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="value" fill="#4facfe">
              <LabelList
                dataKey="name"
                position="top"
                fontSize={10}
                fill="#000"
              />
            </Bar>
            <Line dataKey="value" stroke="#f43f5e" strokeWidth={2} />
          </ComposedChart>
        </ResponsiveContainer>
      );

    case "doughnut":
      return (
        <ResponsiveContainer width="100%" height={380}>
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              innerRadius={60}
              outerRadius={120}
              label={(d) => `${d.name} (${d.percent}%)`}
            >
              {data.map((d, i) => (
                <Cell key={i} fill={COLORS[i % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>
      );

    case "crosstab":
      // backward compatibility if someone passes chart type 'crosstab'
      return <CrosstabTable kpi={kpiLike} />;

    case "pictogram":
      return <PictogramRenderer data={data} adminType={kpiLike.adminType} />;

    default:
      // console.log ("⚠️ Unknown chart type:", chart);
      return <div>Unknown chart type: {type}</div>;
  }
}
